import { useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { Truck, ArrowRight, Info } from "lucide-react";
import Reveal from "@/components/site/Reveal";

const LOADS = [
  {
    id: "single",
    label: "Single Item",
    desc: "A couch, fridge, mattress, or that one thing in the way",
    fill: 12,
    range: [79, 129],
  },
  {
    id: "quarter",
    label: "1/4 Truckload",
    desc: "A few items or a small pile of debris",
    fill: 32,
    range: [150, 250],
  },
  {
    id: "half",
    label: "1/2 Truckload",
    desc: "Garage corner, small room cleanout, yard pile",
    fill: 58,
    range: [300, 450],
  },
  {
    id: "full",
    label: "Full Truckload",
    desc: "Full garage, estate cleanout, big demo debris",
    fill: 100,
    range: [500, 750],
  },
];

export default function Estimator() {
  const [selected, setSelected] = useState(LOADS[1]);

  const goToQuote = (e) => {
    e.preventDefault();
    window.__lenis
      ? window.__lenis.scrollTo("#quote", { offset: -72 })
      : document.querySelector("#quote")?.scrollIntoView({ behavior: "smooth" });
  };

  return (
    <section
      id="pricing"
      data-testid="pricing-section"
      className="mx-auto max-w-7xl px-5 py-28 sm:px-8 sm:py-36"
    >
      <div className="grid gap-14 lg:grid-cols-2 lg:gap-20">
        <Reveal>
          <p className="font-mono-ind text-xs uppercase tracking-[0.3em] text-[#FF9F00]">
            Ballpark Pricing
          </p>
          <h2 className="mt-4 font-display text-5xl leading-[0.95] tracking-tight sm:text-7xl">
            KNOW BEFORE
            <br />
            <span className="text-[#FF5A00]">WE SHOW.</span>
          </h2>
          <p className="mt-6 max-w-md text-sm leading-relaxed text-zinc-400 sm:text-base">
            Fair, upfront pricing is the GW promise. Pick a load size to see a
            ballpark — your exact quote is always free and confirmed before we
            lift a thing.
          </p>

          <div className="mt-8 flex items-start gap-3 border border-[#282C34] bg-[#14171C] p-5">
            <Info className="mt-0.5 h-4 w-4 shrink-0 text-[#FF9F00]" />
            <p className="text-xs leading-relaxed text-zinc-500">
              Heavy materials (concrete, shingles, dirt) and specialty items (hot
              tubs, pianos, sheds) may adjust the range. You always approve the
              final price on site — no surprises, ever.
            </p>
          </div>
        </Reveal>

        <Reveal delay={0.1}>
          <div className="relative border border-[#282C34] bg-[#14171C] p-6 sm:p-10">
            <div className="hazard absolute top-0 left-0 right-0 h-1 opacity-70" />

            {/* Truck fill visual */}
            <div className="flex items-center gap-4">
              <Truck className="h-8 w-8 shrink-0 text-[#FF9F00]" strokeWidth={1.75} />
              <div className="h-4 flex-1 overflow-hidden border border-[#282C34] bg-[#0B0C0E]">
                <motion.div
                  data-testid="pricing-truck-fill"
                  className="h-full bg-gradient-to-r from-[#FF5A00] to-[#FF9F00]"
                  animate={{ width: `${selected.fill}%` }}
                  transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
                />
              </div>
              <span className="w-12 text-right font-mono-ind text-xs text-zinc-500">
                {selected.fill}%
              </span>
            </div>

            <div className="mt-8 grid gap-2 sm:grid-cols-2">
              {LOADS.map((l) => (
                <button
                  key={l.id}
                  type="button"
                  data-testid={`pricing-option-${l.id}`}
                  onClick={() => setSelected(l)}
                  className={`border p-5 text-left transition-all duration-300 ${
                    selected.id === l.id
                      ? "border-[#FF5A00] bg-[#FF5A00]/10"
                      : "border-[#282C34] bg-[#0B0C0E] hover:border-[#FF5A00]/50"
                  }`}
                >
                  <p
                    className={`font-display text-xl tracking-wide ${
                      selected.id === l.id ? "text-[#FF9F00]" : "text-zinc-200"
                    }`}
                  >
                    {l.label}
                  </p>
                  <p className="mt-1 text-xs leading-relaxed text-zinc-500">{l.desc}</p>
                  <p className="mt-2 font-mono-ind text-sm font-bold text-zinc-300">
                    ${l.range[0]}–${l.range[1]}
                  </p>
                </button>
              ))}
            </div>

            <div className="mt-8 flex flex-wrap items-end justify-between gap-6 border-t border-[#282C34] pt-8">
              <div>
                <p className="font-mono-ind text-[10px] uppercase tracking-[0.25em] text-zinc-500">
                  Your ballpark
                </p>
                <div className="mt-1 flex items-baseline gap-1 overflow-hidden">
                  <AnimatePresence mode="popLayout">
                    <motion.span
                      key={selected.id}
                      data-testid="pricing-estimate"
                      initial={{ y: 28, opacity: 0 }}
                      animate={{ y: 0, opacity: 1 }}
                      exit={{ y: -28, opacity: 0 }}
                      transition={{ duration: 0.45, ease: [0.22, 1, 0.36, 1] }}
                      className="font-display text-6xl leading-none text-[#FF5A00]"
                    >
                      ${selected.range[0]}–${selected.range[1]}
                    </motion.span>
                  </AnimatePresence>
                </div>
              </div>
              <a
                href="#quote"
                onClick={goToQuote}
                data-testid="pricing-quote-cta"
                className="glow-orange group inline-flex items-center gap-3 bg-[#FF5A00] px-7 py-4 font-display text-xl tracking-widest text-[#0B0C0E] transition-all duration-300 hover:-translate-y-0.5 hover:bg-[#FF9F00]"
              >
                LOCK IN MY FREE QUOTE
                <ArrowRight className="h-5 w-5 transition-transform duration-300 group-hover:translate-x-1" />
              </a>
            </div>
          </div>
        </Reveal>
      </div>
    </section>
  );
}
